import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useGameStore, useUserStore } from '../../store';
import { useNavigateByExp } from '../../hooks';
import { Canvas, RootState } from '@react-three/fiber';
import { STAGES } from '../../config';
import { Scene } from './components/Scene';
import { useAssetsPreloader } from './hooks/useAssetsPreloader';
import { usePrecompileRenderer } from './hooks/useGLTF';
import { getViewByGameMode } from './views';
import { useOwlBattleService } from './hooks/useOwlBattleService';
import { Layer } from './components/composition/Layer';
import { useSocketIOContext } from '../SocketIOContext';
import { useNavigate } from 'react-router';
import { useAchievementListener } from '../../hooks/useAchievementListener';
import { useErrorListener } from "../../hooks/useErrorListener";
import { getPlatformData } from './utils/getPlatformData';
import { isTouchDevice } from '../../utils/fixIosMobileMagnifier';

export const GameSceneNew = () => {
  const navigate = useNavigate();
  const { setCurrentPointerCoords, gameMode, setGameMode, setGameStage } = useGameStore();
  const stage = useGameStore((state) => state.gameStage);
  const activeView = getViewByGameMode(gameMode);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const webSocketContext = useSocketIOContext();
  const { performanceClass } = getPlatformData();

  // Preload assets, compile the renderer, and start battle & achievement/error listeners.
  useAssetsPreloader({ viewId: activeView });
  useNavigateByExp();
  usePrecompileRenderer(rendererRef.current);
  useOwlBattleService();
  useAchievementListener();
  useErrorListener();

  const totalExp = useUserStore((state) => state.user.totalExp);

  useEffect(() => {
    if (totalExp >= STAGES[stage + 1]) {
      setGameStage(STAGES[stage + 1]);
    }
  }, [totalExp, stage, setGameStage]);

  // Instead of parsing a JSON string, we now assume that the message is already an object.
  useEffect(() => {
    if (!webSocketContext.lastMessage) {
      return;
    }

    const message = webSocketContext.lastMessage;
    if (message.type === 'FIGHT_UPDATE' && gameMode !== 'battle') {
      setGameMode('battle');
      navigate('/fight');
    }
  }, [webSocketContext.lastMessage, gameMode, navigate, setGameMode]);

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.buttons !== 1) return;
    setCurrentPointerCoords({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!e.touches.length) return;
    setCurrentPointerCoords({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY, // Fixed: Use clientY for vertical coordinate.
    });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setCurrentPointerCoords({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!e.changedTouches.length) return;
    setCurrentPointerCoords({
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
    });
  };

  const onCreated = (state: RootState) => {
    rendererRef.current = state.gl;
  };

  return (
    <div className="h-dvh w-full z-0 absolute">
      <Canvas
        shadows={false}
        gl={{
          antialias: false,
          powerPreference: ['LOW', 'AVERAGE'].includes(performanceClass)
            ? 'low-power'
            : 'high-performance',
        }}
        onCreated={onCreated}
        onPointerMove={!isTouchDevice ? onPointerMove : undefined}
        onPointerUp={!isTouchDevice ? onPointerUp : undefined}
        onTouchMove={isTouchDevice ? onTouchMove : undefined}
        onTouchEnd={isTouchDevice ? onTouchEnd : undefined}
      >
        <Layer renderOrder={0}>
          <Scene />
          {/* <DebugScene /> */}
        </Layer>
        <Layer renderOrder={1} />
      </Canvas>
    </div>
  );
};
